import { Product, ProductImage } from 'lib/product';
import { CatalogueItem as CatalogueItemType } from './types';
import { Link, TextBalancer } from 'components/common';
import { getProductPath } from 'lib/router';
import { getProductBySlug } from 'lib/product/api';
import { useState } from 'react';
import { useCart } from 'lib/cart';
import * as utils from 'lib/product/utils';
import * as analytics from 'lib/analytics';
import notification from 'lib/notification';
import { usePopupContext } from 'lib/popups';

interface CatalogueItemProps {
  item: CatalogueItemType;
}

const CatalogueItem = ({ item }: CatalogueItemProps) => {
  const {
    addToCart: _addToCart,
    loading: isCartLoading,
    cart: currentCart,
  } = useCart();
  const segments = item.url.split('/');
  const path = segments[segments.length - 1];
  const [slug, query] = path.split('?');
  const { triggerPostAddToCartPopup } = usePopupContext();

  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const findProduct = async (): Promise<Product> => {
    try {
      const { error, response } = await getProductBySlug(slug, {
        rawError: true,
      });

      if (error) throw error;

      return response.data as Product;
    } catch (err) {
      throw err;
    }
  };

  const addToCart = async () => {
    try {
      setIsAddingToCart(true);

      const quantity = 1;
      const product = await findProduct();
      if (!product) return;

      const productVariant = utils.getDisplayingProductVariant(
        product,
      );

      const isOutOfStock = productVariant?.stock === 0;

      if (isOutOfStock) throw new Error('Out of stock');

      if (quantity > productVariant.stock)
        throw new Error('Not enough stock');

      const keyValuePairs = query.split('&');
      const addonOptionValueIds: number[] = [];

      keyValuePairs.forEach((pair) => {
        const [_, value] = pair.split('=');

        addonOptionValueIds.push(Number(value));
      });

      const updatedCart = await _addToCart({
        product,
        productVariantId: productVariant.id,
        quantity: 1,
        addonOptionValueIds: addonOptionValueIds ?? [],
      });

      if (currentCart.items.length === 0 && !updatedCart.user_id) {
        triggerPostAddToCartPopup();
      }

      analytics.addToCart({
        product,
        productVariant,
        quantity,
        updatedCart,
      });

      notification.success('YAY! Successfully added to cart!');
    } catch (err) {
      if (err?.response?.status === 404) {
        notification.error(
          `Oh no, ${item.title} is out of stock and can't be added to the cart.`,
        );
      } else {
        notification.error(err.message ?? err.errors?.message);
      }
    } finally {
      setIsAddingToCart(false);
    }
  };

  return (
    <div className="product">
      <figure className="product-media">
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <Link
            to={`${getProductPath(slug)}${query ? `?${query}` : ''}`}
          >
            <ProductImage
              src={item.image_url ?? ''}
              layout="responsive"
              size={375}
              alt={item.title ?? ''}
            />
          </Link>
        </div>
        <div className="product-action action-icon-top">
          <button
            className="btn-product btn-cart"
            onClick={addToCart}
            disabled={isAddingToCart || isCartLoading}
          >
            <span>
              {isCartLoading
                ? 'Loading...'
                : isAddingToCart
                ? 'Adding to Cart...'
                : 'Add to Cart'}
            </span>
          </button>
        </div>
      </figure>
      <div className="product-body product-action-inner">
        <TextBalancer
          as="h3"
          className="product-title tw-text-[1.375rem] md:tw-text-[1.6rem]"
        >
          <Link
            to={`${getProductPath(slug)}${query ? `?${query}` : ''}`}
          >
            <>{item.title ?? ''}</>
          </Link>
        </TextBalancer>
        <div className="product-price tw-text-[1.375rem] md:tw-text-[1.6rem]">
          {item.price ? (
            <>
              $
              {item.price.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </>
          ) : (
            <>&nbsp;</>
          )}
        </div>
        {item.excerpt && (
          <TextBalancer className="tw-text-[11px] sm:tw-text-[12px] tw-hyphens-auto tw-break-words tw-leading-tight">
            {item.excerpt}
          </TextBalancer>
        )}
      </div>
    </div>
  );
};

export default CatalogueItem;
